import { Box, Button, IconButton, Typography, useTheme, useMediaQuery } from "@mui/material";
import {
	Add,
	ContentCopy,
	Delete,
	Download,
	Merge,
	Publish,
	Map,
	Warning,
	CallSplit,
	Edit,
	BorderColor,
} from "@mui/icons-material";
import {
	MRT_GlobalFilterTextField,
	MRT_ToggleFiltersButton,
	MRT_ToggleFullScreenButton,
	MRT_TableInstance,
} from "material-react-table";
import type { Job } from "../../../../../models";
import { PortalTooltip } from "../../../../helpers";
import { useEditModeContext } from "../shared/hooks/EditModeContext";

/**
 * Props interface for ImportTableToolbar component
 */
export interface ImportTableToolbarProps {
	// Table instance
	table: MRT_TableInstance<Job>;

	// State
	jobs: Job[];

	// Map controls - new mode-based API
	mapDisplayMode?: "modal" | "drawer" | null;
	setMapDisplayMode?: (mode: "modal" | "drawer" | null) => void;

	// Legacy map controls (for backward compatibility)
	isMapVisible?: boolean;
	setIsMapVisible?: (visible: boolean) => void;

	hasSelectedRows?: boolean;
	selectedRowCount?: number;

	// Lasso selection state for Split feature
	selectedFeatureIds?: Set<string>;
	selectedJobId?: string;

	// Callbacks
	hasValidationErrors: () => boolean;
	exportCSV: () => void;
	createJobs: () => Promise<void>;
	addBlankJob: () => void;
	deleteSelectedJobs: () => void;
	duplicateSelectedJobs: () => void;
	mergeSelectedJobs: () => void;
	splitSelectedJob?: () => void;
	bulkEditSelectedJobs?: () => void;
}

/**
 * Toolbar component for ImportTable with global search, filters, and action buttons.
 *
 * Features:
 * - Global search field
 * - Filter toggle
 * - Fullscreen toggle
 * - CSV export button
 * - Create Jobs button (disabled when validation errors exist)
 * - Map view buttons (modal and drawer options)
 * - Error message display when validation fails
 */
export const ImportTableToolbar: React.FC<ImportTableToolbarProps> = ({
	table,
	jobs,
	mapDisplayMode,
	setMapDisplayMode,
	isMapVisible,
	setIsMapVisible,
	hasSelectedRows,
	selectedRowCount = 0,
	selectedFeatureIds,
	hasValidationErrors,
	exportCSV,
	createJobs,
	addBlankJob,
	deleteSelectedJobs,
	duplicateSelectedJobs,
	mergeSelectedJobs,
	splitSelectedJob,
	bulkEditSelectedJobs,
}) => {
	// Determine if we're using the new or legacy API
	const hasNewMapAPI = setMapDisplayMode !== undefined;
	const hasLegacyMapAPI = setIsMapVisible !== undefined;
	const showMapControls = hasNewMapAPI || hasLegacyMapAPI;

	// Mobile detection for responsive map button display
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// Edit mode context for "Edit All" button
	const { editingRowIds, enterAllEditMode, exitAllEditMode } = useEditModeContext();
	const isAnyRowEditing = editingRowIds.size > 0;

	const handleModalClick = () => {
		if (hasNewMapAPI) {
			setMapDisplayMode(mapDisplayMode === "modal" ? null : "modal");
		} else if (hasLegacyMapAPI) {
			setIsMapVisible(!isMapVisible);
		}
	};

	const handleDrawerClick = () => {
		if (hasNewMapAPI) {
			setMapDisplayMode(mapDisplayMode === "drawer" ? null : "drawer");
		}
	};

	const isModalActive = hasNewMapAPI ? mapDisplayMode === "modal" : isMapVisible;
	const isDrawerActive = hasNewMapAPI ? mapDisplayMode === "drawer" : false;

	return (
		<Box className={`toolbar-container${isDrawerActive ? " map-open" : ""}`}>
			<Box className="toolbar-left">
				{/* import MRT sub-components */}
				<MRT_GlobalFilterTextField table={table} />
				<MRT_ToggleFiltersButton table={table} />
				{hasValidationErrors() && (
					<>
						<Typography color="primary" className="error-message desktop-only">
							Double-click to edit
						</Typography>
						<Typography color="error" className="error-message desktop-only">
							Fill out all required data
						</Typography>
						<PortalTooltip title="Fill out all required data">
							<Warning color="error" className="error-icon mobile-only" />
						</PortalTooltip>
					</>
				)}
			</Box>
			<Box className="toolbar-right">
				<Box className="toolbar-actions">
					{/* Map view buttons - responsive: modal on mobile, drawer on desktop */}
					{showMapControls && hasNewMapAPI && (
						<>
							{/* Modal button - mobile only, hidden when drawer is open */}
							{isMobile && !isDrawerActive && (
								<PortalTooltip
									title={
										!hasSelectedRows
											? "Select jobs to view them on a map"
											: isModalActive
												? "Close map"
												: "Show jobs on map"
									}
								>
									<span>
										<IconButton
											color="primary"
											onClick={handleModalClick}
											disabled={!hasSelectedRows && !isModalActive}
										>
											<Map />
										</IconButton>
									</span>
								</PortalTooltip>
							)}
							{/* Drawer button - desktop only */}
							{!isMobile && (
								<PortalTooltip
									title={
										!hasSelectedRows
											? "Select jobs to view them on a map"
											: isDrawerActive
												? "Close map"
												: "Show jobs on map"
									}
								>
									<span style={{ display: "flex" }}>
										<Button
											color="primary"
											variant="text"
											onClick={handleDrawerClick}
											disabled={!hasSelectedRows && !isDrawerActive}
										>
											<Map className="icon-in-button" />
											<span className="button-text">Map</span>
										</Button>
									</span>
								</PortalTooltip>
							)}
						</>
					)}
					{/* Edit All button - toggle all rows in/out of edit mode */}
					<PortalTooltip title={isAnyRowEditing ? "Exit edit mode" : "Enter edit mode"}>
						<Button
							color="primary"
							variant={isAnyRowEditing ? "contained" : "text"}
							onClick={(e) => {
								e.stopPropagation(); // Prevent click-away from immediately exiting
								if (isAnyRowEditing) {
									exitAllEditMode();
								} else {
									// Include both leaf row IDs (job UUIDs) and group row IDs (e.g., "grower:Name")
									const allRowIds = table.getRowModel().flatRows.map((r) => r.id);
									enterAllEditMode(allRowIds);
								}
							}}
						>
							<BorderColor className="icon-in-button" />
							<span className="button-text">{isAnyRowEditing ? "Done" : "Edit All"}</span>
						</Button>
					</PortalTooltip>
					{/* Add button */}
					<PortalTooltip title="Add a new blank job">
						<Button color="primary" variant="text" onClick={addBlankJob}>
							<Add className="icon-in-button" />
							<span className="button-text">Add</span>
						</Button>
					</PortalTooltip>
					{/* Delete button */}
					<PortalTooltip title={hasSelectedRows ? "Delete selected jobs" : "Select jobs to delete"}>
						<span style={{ display: "flex" }}>
							<Button color="primary" variant="text" onClick={deleteSelectedJobs} disabled={!hasSelectedRows}>
								<Delete className="icon-in-button" />
								<span className="button-text">Delete</span>
							</Button>
						</span>
					</PortalTooltip>
					{/* Duplicate button */}
					<PortalTooltip title={hasSelectedRows ? "Duplicate selected jobs" : "Select jobs to duplicate"}>
						<span style={{ display: "flex" }}>
							<Button color="primary" variant="text" onClick={duplicateSelectedJobs} disabled={!hasSelectedRows}>
								<ContentCopy className="icon-in-button" />
								<span className="button-text">Duplicate</span>
							</Button>
						</span>
					</PortalTooltip>
					{/* Bulk Edit button */}
					<PortalTooltip
						title={
							selectedRowCount === 0
								? "Select jobs to edit"
								: selectedRowCount === 1
									? "Select 2+ jobs to bulk edit, or double-click row to edit single job"
									: `Edit ${selectedRowCount} jobs at once`
						}
					>
						<span style={{ display: "flex" }}>
							<Button color="primary" variant="text" onClick={bulkEditSelectedJobs} disabled={selectedRowCount < 2}>
								<Edit className="icon-in-button" />
								<span className="button-text">Edit</span>
							</Button>
						</span>
					</PortalTooltip>
					{/* Split button - enabled when exactly 1 job selected AND features are selected via lasso */}
					<PortalTooltip
						title={
							selectedRowCount === 0
								? "Select 1 job to split"
								: selectedRowCount > 1
									? "Select only 1 job to split"
									: !selectedFeatureIds?.size
										? "Use lasso tool to select features, then split"
										: "Split job by selected features"
						}
					>
						<span style={{ display: "flex" }}>
							<Button
								color="primary"
								variant="text"
								onClick={splitSelectedJob}
								disabled={selectedRowCount !== 1 || !selectedFeatureIds?.size}
							>
								<CallSplit className="icon-in-button" />
								<span className="button-text">Split</span>
							</Button>
						</span>
					</PortalTooltip>
					{/* Merge button */}
					<PortalTooltip title={selectedRowCount >= 2 ? "Merge selected jobs" : "Select at least 2 jobs to merge"}>
						<span style={{ display: "flex" }}>
							<Button color="primary" variant="text" onClick={mergeSelectedJobs} disabled={selectedRowCount < 2}>
								<Merge className="icon-in-button" />
								<span className="button-text">Merge</span>
							</Button>
						</span>
					</PortalTooltip>
					{/* CSV button */}
					<PortalTooltip title="Download table as a CSV for easier double-checking. This CSV is not used by Rogo.">
						<span style={{ display: "flex" }}>
							<Button color="primary" variant="text" onClick={exportCSV} disabled={jobs.length === 0}>
								<Download className="icon-in-button" />
								<span className="button-text">CSV</span>
							</Button>
						</span>
					</PortalTooltip>
					{/* Submit Jobs button */}
					<PortalTooltip title="Submit jobs to Rogo">
						<span style={{ display: "flex" }}>
							<Button color="primary" variant="contained" onClick={createJobs} disabled={hasValidationErrors()}>
								<Publish className="icon-in-button" />
								<span className="button-text">Submit Jobs</span>
							</Button>
						</span>
					</PortalTooltip>
					<MRT_ToggleFullScreenButton table={table} />
				</Box>
			</Box>
		</Box>
	);
};
